import React from 'react';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { FormField } from '../../../global/form/form-field/form-field';
import { SPACE_IN_BEGIN_REG_EXP } from '../../../../constants/regex.constants';
import { CommonPopupBodyFooterLayout } from '../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../global/sub-window/common/common-close-sub-window-buttons';
import './theme-form-presentation.scss';
import { ThemeFormVM } from '../theme-form.viewmodel';
import { MultiSelectButton } from '../../../global/multi-select/multi-select-button';
import OECheckbox from '../../../global/oe-checkbox/oe-checkbox';
import { Organization } from '../../../_events/_manage-event/shared/presenters-list/presenters-list';
import UploadButton, { FILE_TYPE } from '../../../_events/_manage-event/shared/upload-button';
import { ADD_TEXT, addText, APPLY_TEXT, CANCEL_TEXT, cancelText } from '../../../../constants/common-labels.constants';

interface IProps {
    vm: ThemeFormVM;
    intl: IntlShape;
}

const ThemeFormPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const { intl, vm } = props;
    const formDataVM = vm.getFormDataVM();

    const formSubmitHandler = () => {
        return vm.handleSubmit(intl);
    };

    if (vm.isLoading) {
        return <CommonPopupBodyFooterLayout
            body={
                <div className='oe-form meeting-form'>
                    <FormattedMessage
                        description='Label text'
                        defaultMessage='Loading theme data{suffix}'
                        id='oe.theme.loadingData'
                        values={{
                            suffix: '...'
                        }}
                    />
                </div>
            }
        />;
    }

    const uploadZipFileHandler = (file: File) => {
        formDataVM.setThemeArchive(file);
    };

    return (
        <CommonPopupBodyFooterLayout
            body={
                <div className='theme-form'>
                    <div className='oe-form'>
                        {<div className='oe-form__body'>
                            <FormField
                                label={intl.formatMessage(
                                    {
                                        id: 'oe.theme.themeName',
                                        description: 'Theme name title',
                                        defaultMessage: 'Theme Name'
                                    })
                                }>
                                <input
                                    type='text'
                                    className='oe-form-field__control'
                                    value={formDataVM.name}
                                    onChange={({ target: { value } }) => formDataVM.setName(value.replace(SPACE_IN_BEGIN_REG_EXP, ''))}
                                />
                                {formDataVM.showValidationErrors() ? <div
                                    className='oe-form-field__error'>{formDataVM.getThemeNameError(intl)}</div> : null}
                                {formDataVM.serverErrorMessage &&
                                <div className='oe-form-field__error'>{formDataVM.serverErrorMessage}</div>}
                            </FormField>
                            <FormField
                                label={intl.formatMessage(
                                    {
                                        id: 'oe.theme.ThemeArchive',
                                        description: 'Placeholder text',
                                        defaultMessage: 'Theme Code (ZIP File)'
                                    })
                                }>
                                <UploadButton
                                    fileName={formDataVM.themeArchive?.name}
                                    onChange={uploadZipFileHandler}
                                    uploadedFileType={FILE_TYPE.ZIP}
                                />
                                {formDataVM.showValidationErrors()
                                    ?
                                    <div className='oe-form-field__error'>{formDataVM.getThemeArchiveError(intl)}</div>
                                    : null
                                }
                            </FormField>

                            <div className='theme-form__accounts-selector'>
                                <div className='theme-form__accounts-selector-title'>
                                    <FormattedMessage
                                        id='oe.accounts.accountsTitle'
                                        description='Accounts title'
                                        defaultMessage='Accounts'
                                    />
                                    {formDataVM.assignedAccounts.length > 0 &&
                                    <div className='theme-form__accounts-selector__accounts-list'>
                                        {
                                            formDataVM.assignedAccounts
                                                .slice()
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map(account => {
                                                    return (
                                                        <Organization
                                                            key={account.accountId}
                                                            name={account.name}
                                                            organizationId={account.accountId}
                                                            removeAttendee={
                                                                mobx.action(() => formDataVM.removeAccount(formDataVM.assignedAccounts, account.accountId))
                                                            }
                                                            disabled={formDataVM.isAllAccountsChecked}
                                                        />
                                                    );
                                                })
                                        }
                                    </div>
                                    }
                                    <MultiSelectButton
                                        items={formDataVM.selectableAccounts}
                                        disabledButton={formDataVM.isAllAccountsChecked}
                                        value={formDataVM.assignedAccounts}
                                        keyProperty='accountId'
                                        nameProperty='name'
                                        filterHandler={formDataVM.searchAccounts}
                                        text={
                                            <FormattedMessage
                                                id='oe.accounts.add'
                                                description='Button text'
                                                defaultMessage='{prefix}Add Accounts'
                                                values={{
                                                    prefix: '+ '
                                                }}
                                            />
                                        }
                                        applyButtonText={addText()}
                                        resetButtonText={cancelText()}
                                        onChange={formDataVM.setAssignedAccounts}
                                    />

                                    <OECheckbox
                                        label={<FormattedMessage
                                            id='oe.accounts.allAccounts'
                                            defaultMessage='All Accounts'
                                            description='Label text'
                                        />}
                                        checked={formDataVM.isAllAccountsChecked}
                                        onChange={() => formDataVM.handleAllAccountsCheckbox(formDataVM.isAllAccountsChecked)}
                                        classNames='oe-form-field__checkbox theme-form__checkbox'
                                    />
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                </div>
            }
            footer={
                <>
                    {vm.isSaving() &&
                    <span className='oe-popup-layout__footer-status-area'>
                            {intl.formatMessage({
                                id: 'oe.theme.themeIsBeingSaved',
                                description: 'Placeholder about saving theme',
                                defaultMessage: 'Theme is being saved, please wait...'
                            })}
                        </span>
                    }

                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={intl.formatMessage(vm.theme ? APPLY_TEXT : ADD_TEXT)}
                            modifier={['fat', 'confirm-safe']}
                            onClick={formSubmitHandler}
                            disabled={vm.isSaving()}
                        />
                        <DefaultCloseSubWindowButton
                            text={intl.formatMessage(CANCEL_TEXT)}
                        />
                    </div>
                </>
            }
        />
    );

}));

export { ThemeFormPresentation };
